import { diffWordsWithSpace, diffSentences } from "diff";
import OfficeSettings from "@/constants/office";
import { STORAGE_KEY_PREFIX } from "@/constants";
import { OfficeEnvironment } from "@/types/document";
import log from "@/utils/logger";

export const diffCutoffStorageKey = `${STORAGE_KEY_PREFIX}diffCutoff`;
export const DIFF_CUTOFF_DEFAULT = "0.35";

export enum ChangeType {
  Add = "add",
  Remove = "remove",
  Ignore = "ignore",
}

export interface DiffChangeResult {
  type: ChangeType;
  text: string;
}

export interface DiffWithMetadata {
  changes: DiffChangeResult[];
  usedSentenceDiff: boolean;
  changeRatio: number;
}

function countChanges(diffs: Array<{ added?: boolean; removed?: boolean; value: string }>): {
  additions: number;
  deletions: number;
} {
  let additions = 0;
  let deletions = 0;

  for (const diff of diffs) {
    if (diff.added) additions++;
    if (diff.removed) deletions++;
  }

  return { additions, deletions };
}

function webDiff(original: string, replacement: string): DiffChangeResult[] {
  // First, try word-level diff
  const wordDiffs = diffWordsWithSpace(original, replacement);
  const { additions, deletions } = countChanges(wordDiffs);

  // Get cutoff from localStorage, default to DIFF_CUTOFF_DEFAULT
  const cutoff = parseFloat(localStorage.getItem(diffCutoffStorageKey) || DIFF_CUTOFF_DEFAULT);
  const originalWordCount = original.split(" ").length;
  const changeRatio = originalWordCount > 0 ? (additions + deletions) / originalWordCount : 0;

  log.debug("Diff algorithm decision", "webDiff", {
    cutoff,
    changeRatio,
    additions,
    deletions,
    originalWordCount,
    usingSentenceDiff: changeRatio > cutoff,
    originalLength: original.length,
    replacementLength: replacement.length,
  });

  // If changes exceed threshold, use sentence-level diff
  if (changeRatio > cutoff) {
    return diffSentences(original, replacement).map(({ added, removed, value }) => {
      if (added) return { type: ChangeType.Add, text: value };
      if (removed) return { type: ChangeType.Remove, text: value };
      return { type: ChangeType.Ignore, text: value };
    });
  }

  // Otherwise, use word-level diff
  return wordDiffs.map(({ added, removed, value }) => {
    if (added) return { type: ChangeType.Add, text: value };
    if (removed) return { type: ChangeType.Remove, text: value };
    return { type: ChangeType.Ignore, text: value };
  });
}

function splitOnWhitespace(text: string, maxLength: number): string[] {
  const chunks: string[] = [];
  let currentStart = 0;

  while (currentStart < text.length) {
    let chunkEnd = currentStart + maxLength;

    if (chunkEnd >= text.length) {
      chunks.push(text.substring(currentStart).trim());
      break;
    }

    let lastWhitespaceIndex = -1;
    for (let i = chunkEnd; i > currentStart; i--) {
      if (/\s/.test(text[i])) {
        lastWhitespaceIndex = i;
        break;
      }
    }

    if (lastWhitespaceIndex !== -1) {
      chunks.push(text.substring(currentStart, lastWhitespaceIndex).trim());
      currentStart = lastWhitespaceIndex + 1;
    } else {
      chunks.push(text.substring(currentStart, chunkEnd).trim());
      currentStart = chunkEnd;
    }

    while (currentStart < text.length && /\s/.test(text[currentStart])) {
      currentStart++;
    }
  }

  return chunks.filter((chunk) => chunk.length > 0);
}

function desktopDiff(original: string, replacement: string): DiffChangeResult[] {
  // First, try word-level diff
  const wordDiffs = diffWordsWithSpace(original, replacement);
  const { additions, deletions } = countChanges(wordDiffs);

  // Get cutoff from localStorage, default to DIFF_CUTOFF_DEFAULT
  const cutoff = parseFloat(localStorage.getItem(diffCutoffStorageKey) || DIFF_CUTOFF_DEFAULT);
  const originalWordCount = original.split(" ").length;
  const changeRatio = originalWordCount > 0 ? (additions + deletions) / originalWordCount : 0;

  log.debug("Diff algorithm decision", "desktopDiff", {
    cutoff,
    changeRatio,
    additions,
    deletions,
    originalWordCount,
    usingSentenceDiff: changeRatio > cutoff,
    originalLength: original.length,
    replacementLength: replacement.length,
  });

  // If changes exceed threshold, use sentence-level diff
  const diffs = changeRatio > cutoff ? diffSentences(original, replacement) : wordDiffs;

  const result: DiffChangeResult[] = [];
  const MAX_LENGTH = 255;

  diffs.forEach(({ added, removed, value }) => {
    if (added) {
      result.push({ type: ChangeType.Add, text: value });
    } else if (removed) {
      if (value.length > MAX_LENGTH) {
        const chunks = splitOnWhitespace(value, MAX_LENGTH);
        chunks.forEach((chunk) => {
          result.push({ type: ChangeType.Remove, text: chunk });
        });
      } else {
        result.push({ type: ChangeType.Remove, text: value });
      }
    } else {
      if (value.length > MAX_LENGTH) {
        const chunks = splitOnWhitespace(value, MAX_LENGTH);
        chunks.forEach((chunk) => {
          result.push({ type: ChangeType.Ignore, text: chunk });
        });
      } else {
        result.push({ type: ChangeType.Ignore, text: value });
      }
    }
  });

  return result;
}

export const diff = OfficeSettings.environment === OfficeEnvironment.Desktop ? desktopDiff : webDiff;

function webDiffWithMetadata(original: string, replacement: string): DiffWithMetadata {
  // First, try word-level diff
  const wordDiffs = diffWordsWithSpace(original, replacement);
  const { additions, deletions } = countChanges(wordDiffs);

  // Get cutoff from localStorage, default to DIFF_CUTOFF_DEFAULT
  const cutoff = parseFloat(localStorage.getItem(diffCutoffStorageKey) || DIFF_CUTOFF_DEFAULT);
  const originalWordCount = original.split(" ").length;
  const changeRatio = originalWordCount > 0 ? (additions + deletions) / originalWordCount : 0;
  const usedSentenceDiff = changeRatio > cutoff;

  log.debug("Diff with metadata algorithm decision", "webDiffWithMetadata", {
    cutoff,
    changeRatio,
    additions,
    deletions,
    originalWordCount,
    usedSentenceDiff,
    originalLength: original.length,
    replacementLength: replacement.length,
  });

  // If changes exceed threshold, use sentence-level diff
  const diffs = usedSentenceDiff ? diffSentences(original, replacement) : wordDiffs;

  const changes = diffs.map(({ added, removed, value }) => {
    if (added) return { type: ChangeType.Add, text: value };
    if (removed) return { type: ChangeType.Remove, text: value };
    return { type: ChangeType.Ignore, text: value };
  });

  return { changes, usedSentenceDiff, changeRatio };
}

function desktopDiffWithMetadata(original: string, replacement: string): DiffWithMetadata {
  // First, try word-level diff
  const wordDiffs = diffWordsWithSpace(original, replacement);
  const { additions, deletions } = countChanges(wordDiffs);

  // Get cutoff from localStorage, default to DIFF_CUTOFF_DEFAULT
  const cutoff = parseFloat(localStorage.getItem(diffCutoffStorageKey) || DIFF_CUTOFF_DEFAULT);
  const originalWordCount = original.split(" ").length;
  const changeRatio = originalWordCount > 0 ? (additions + deletions) / originalWordCount : 0;
  const usedSentenceDiff = changeRatio > cutoff;

  log.debug("Diff with metadata algorithm decision", "desktopDiffWithMetadata", {
    cutoff,
    changeRatio,
    additions,
    deletions,
    originalWordCount,
    usedSentenceDiff,
    originalLength: original.length,
    replacementLength: replacement.length,
  });

  // If changes exceed threshold, use sentence-level diff
  const diffs = usedSentenceDiff ? diffSentences(original, replacement) : wordDiffs;

  const changes: DiffChangeResult[] = [];
  const MAX_LENGTH = 255;

  diffs.forEach(({ added, removed, value }) => {
    if (added) {
      changes.push({ type: ChangeType.Add, text: value });
    } else if (removed) {
      if (value.length > MAX_LENGTH) {
        const chunks = splitOnWhitespace(value, MAX_LENGTH);
        chunks.forEach((chunk) => {
          changes.push({ type: ChangeType.Remove, text: chunk });
        });
      } else {
        changes.push({ type: ChangeType.Remove, text: value });
      }
    } else {
      if (value.length > MAX_LENGTH) {
        const chunks = splitOnWhitespace(value, MAX_LENGTH);
        chunks.forEach((chunk) => {
          changes.push({ type: ChangeType.Ignore, text: chunk });
        });
      } else {
        changes.push({ type: ChangeType.Ignore, text: value });
      }
    }
  });

  return { changes, usedSentenceDiff, changeRatio };
}

export const diffWithMetadata =
  OfficeSettings.environment === OfficeEnvironment.Desktop ? desktopDiffWithMetadata : webDiffWithMetadata;
